import { Component, Input, Inject, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { RoundingSettingsService } from 'src/app/modules/settings/services/rounding-settings.service';
import { RoundingSettings } from 'src/app/core/interfaces/rounding-settings.interface';

@Component({
  selector: 'app-text-input-row',
  templateUrl: './text-input-row.component.html',
  styleUrl: './text-input-row.component.scss'
})
export class TextInputRowComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: false }) label?: string;
  @Input({ required: true }) placeholder: string;
  @Input({ required: true }) errorMessage: string;
  @Input({ required: true }) control: FormControl;
  @Input({ required: false }) type: "text" | "number" | "password" = 'text';
  @Input({ required: false }) min?: number | string;
  @Input({ required: false }) max?: number | string;
  @Input({ required: false }) step?: number | string;
  @Input({ required: false }) inputmode?: string;
  @Input({ required: false }) pattern?: string;
  @Input({ required: false }) width = "25%";
  /** When true, label is beside input; when false (default), label is on top and input below. */
  @Input({ required: false }) labelBesideInput = false;
  @Input({ required: false }) isResponsive = true;
  @Input({ required: false }) formatAsMoney = false;
  /** When provided, overrides the global rounding-settings decimal length for money mask. */
  @Input({ required: false }) decimalPlaces?: number;

  @Input() iconPath?: string;
  @Input() iconAlt = 'icon';
  @Output() iconClick = new EventEmitter<void>();

  @Input() tabWhenEnter = true;

  @Input() separator = "";
  @Input() disabled = false;

  isImageIcon(icon?: string): boolean {
    if (!icon) return false;
    return icon.includes('.') || icon.includes('/') || icon.startsWith('data:image');
  }

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Consistent label width when labelBesideInput (avoids passing width everywhere). */
  get labelWidth(): string {
    return this.width || '25%';
  }

  isInvalid(): boolean {
    const control = this.control;
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private roundingSettingsService: RoundingSettingsService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['decimalPlaces'] && this.formatAsMoney) {
      const val = changes['decimalPlaces'].currentValue;
      if (val !== undefined && val !== null) {
        this.separator = `separator.${val}`;
      }
    }
  }

  ngOnInit(): void {
    // Initialize separator from cached settings
    this.updateSeparatorFromSettings();

    // Subscribe to rounding settings changes to update separator dynamically
    this.roundingSettingsService.getCurrentRoundingSettings$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings: RoundingSettings | null) => {
        if (settings) {
          this.updateSeparatorFromSettings(settings);
        }
      });

    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (this.formatAsMoney) {
            console.log(res);
          }
        }
      });
  }

  get pageDirection(): string {
    return this.document.documentElement.dir || 'ltr';
  }

  isRtl(): boolean {
    return this.pageDirection.toLowerCase() === 'rtl';
  }

  private updateSeparatorFromSettings(settings?: RoundingSettings): void {
    // If decimalPlaces is explicitly provided, it takes priority over global settings
    if (this.decimalPlaces !== undefined && this.decimalPlaces !== null) {
      this.separator = `separator.${this.decimalPlaces}`;
      return;
    }

    // We now allow up to 6 decimal places by default in input fields to give users 
    // flexibility to enter precise prices (e.g. cost prices), even if display rounding is less.
    let inputPrecision = 6; 

    this.separator = `separator.${inputPrecision}`;
  }
}
